<template>
  <div>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
      :show="loading"
    >

      <b-card>
        <b-form @submit.prevent="applySearch">
          <b-container fluid>
            <b-row class="mb-2">
              <b-col
                md="4"
              >
                <label for="date1">From Date</label>
                <b-form-datepicker
                  id="date1"
                  v-model="dateTimeFilter.fromDate"
                  :min="disableDateAt()"
                />
                <label
                  class="mt-1"
                >
                  From Time</label>
                <b-input-group>
                  <flat-pickr
                    v-model="dateTimeFilter.fromTime"
                    class="form-control"
                    :config="{ enableTime: true, noCalendar: true, time_24hr: true}"
                  />
                  <b-input-group-append>
                    <b-button
                      v-if="dateTimeFilter.fromTime"
                      class="btn-icon"
                      variant="primary"
                      @click="dateTimeFilter.fromTime = ''"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
              <b-col
                md="4"
              >
                <label for="date2">To Date</label>
                <b-form-datepicker
                  id="date2"
                  v-model="dateTimeFilter.toDate"
                  :min="disableDateAt()"
                />
                <label
                  class="mt-1"
                  for="time2"
                >To Time</label>
                <b-input-group>
                  <flat-pickr
                    v-model="dateTimeFilter.toTime"
                    class="form-control"
                    :config="{ enableTime: true, noCalendar: true, time_24hr: true}"
                  />
                  <b-input-group-append>
                    <b-button
                      v-if="dateTimeFilter.toTime"
                      class="btn-icon"
                      variant="primary"
                      @click="dateTimeFilter.toTime = ''"
                    >
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-col>
              <b-col md="4">
                <label for="driver-phone">Search by Driver Phone number</label>
                <b-form-input
                  id="driver-phone"
                  v-model="queryParams.driverPhoneNumber"
                  placeholder="Driver Phone number"
                />
                <div
                  class="mt-2 d-flex justify-content-end align-items-center"
                >
                  <b-button
                    class="mr-1"
                    variant="primary"
                    type="submit"
                  >
                    Search
                  </b-button>
                  <b-button
                    class="mr-1"
                    variant="outline-primary"
                    @click="clearFilter"
                  >
                    Clear
                  </b-button>
                  <b-button
                    class="btn-icon rounded-circle"
                    variant="flat-primary"
                    type="submit"
                  >
                    <feather-icon
                      icon="RefreshCwIcon"
                    />
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-container>
        </b-form>
        <b-table
          hover
          :items="list"
          :fields="tableColumns"
          show-empty
          empty-text="No booking requests"
          class="text-center"
          @row-clicked="redirectToDetails"
        />
        <base-pagination
          v-model="queryParams.page"
          :total="totalBookings"
          @input="fetchBookings"
          @new-per-page="queryParams.perPage = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BTable, BOverlay, BButton, BContainer, BRow, BCol, BForm, BInputGroup, BFormDatepicker, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import {
  reactive, ref, toRef, watch,
} from '@vue/composition-api'
import flatPickr from 'vue-flatpickr-component'
import BasePagination from '@/UI/BasePagination.vue'
import axiosIns from '@/libs/axios'
import snakeCaseToCamelCase from '@/utils/snake-case-to-camcel-caase'
import formatDate from '@/utils/date-format'
import { errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BCard,
    BTable,
    BOverlay,
    BButton,
    BContainer,
    BRow,
    BInputGroupAppend,
    BCol,
    BFormInput,
    BForm,
    BInputGroup,
    BFormDatepicker,
    flatPickr,
    BasePagination,
  },
  setup(props, ctx) {
    const userId = ctx.root.$route.params.id
    const queryParams = reactive({
      perPage: 10,
      page: 1,
      from: undefined,
      to: undefined,
      userId,
      driverPhoneNumber: undefined,
      state: ['COMPLETED', 'CANCELED'],
    })
    const dateTimeFilter = reactive({
      fromDate: undefined,
      fromTime: undefined,
      toDate: undefined,
      toTime: undefined,
    })
    const tableColumns = [
      {
        label: 'Booking Number',
        key: 'number',
      },
      {
        label: 'Rider Name',
        key: 'user',
        formatter: value => `${value?.first_name || ' '} ${value?.last_name || ' '}`,
      },
      {
        label: 'Driver Name',
        key: 'driver',
        formatter: value => `${value?.first_name || ' '} ${value?.last_name || ' '}`,
      },
      {
        label: 'Zone',
        key: 'zone',
        formatter: val => val?.split('::')[1],
      },
      {
        key: 'createdAt',
        label: 'created at',
        formatter: value => formatDate(value),
      },
      {
        label: 'Status',
        key: 'state',
        formatter: val => snakeCaseToCamelCase(val),
      },
      // 'actions',
    ]
    const list = ref([])
    const loading = ref(false)
    const setFilterDateFormat = () => {
      if (dateTimeFilter.fromDate) {
        if (!dateTimeFilter.fromTime) {
          queryParams.from = new Date(`${dateTimeFilter.fromDate} 00:00:00`)
        } else {
          queryParams.from = new Date(`${dateTimeFilter.fromDate} ${dateTimeFilter.fromTime}`)
        }
      }
      if (dateTimeFilter.toDate) {
        if (dateTimeFilter.fromDate) {
          if (!dateTimeFilter.toTime) {
            const now = new Date()
            queryParams.to = new Date(`${dateTimeFilter.toDate} ${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`)
          } else {
            queryParams.to = new Date(`${dateTimeFilter.toDate} ${dateTimeFilter.toTime}`)
          }
          return true
        }
        errorNotification('You should fill the from date and time first')
        return false
      }
      return true
    }

    const totalBookings = ref(0)
    const fetchBookings = async () => {
      try {
        const val = setFilterDateFormat()
        if (!val) return
        loading.value = true
        const result = await axiosIns.get('/booking', {
          params: queryParams,
        })
        const { total, bookings } = result.data.data
        list.value = bookings
        totalBookings.value = total
      } finally {
        loading.value = false
      }
    }

    fetchBookings()

    watch([toRef(queryParams, 'perPage')], () => {
      fetchBookings()
    })

    const redirectToDetails = ev => {
      ctx.root.$router.push(`/bookings/${ev.id}`)
    }

    const applySearch = () => {
      queryParams.page = 1
      fetchBookings()
    }

    const clearFilter = () => {
      // State to all

      queryParams.state = undefined
      dateTimeFilter.fromDate = undefined
      dateTimeFilter.toDate = undefined
      dateTimeFilter.fromTime = undefined
      dateTimeFilter.toTime = undefined
      queryParams.from = undefined
      queryParams.to = undefined
      queryParams.driverPhoneNumber = undefined
      fetchBookings()
    }
    const disableDateAt = () => {
      const now = new Date()
      now.setMonth(now.getMonth() - 1)
      now.setHours(0, 0, 0, 0)
      return now
    }
    return {
      fetchBookings,
      applySearch,
      tableColumns,
      disableDateAt,
      totalBookings,
      dateTimeFilter,
      clearFilter,
      loading,
      redirectToDetails,
      list,
      queryParams,
    }
  },
}
</script>

    <style scoped>
    </style>
